import React, { useState, useRef, useEffect } from "react";
import { Bot, User, ChevronRight, Send } from "lucide-react";
import { Card } from "../ui/card";

const ChatbotVeiw = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const [threadId, setThreadId] = useState(null);
  const [showQuickActions, setShowQuickActions] = useState(true);

  useEffect(() => {
    const createThread = async () => {
      console.log("Attempting to create a new thread...");

      const response = await fetch(
        "https://eyonyjgai6.execute-api.us-east-1.amazonaws.com/thread",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      const newThreadId = data.threadId;

      console.log("Generated Thread ID:", newThreadId); // This will log the thread ID to the console.

      if (newThreadId) {
        setThreadId(newThreadId);
        sessionStorage.setItem("chatThreadId", newThreadId);
      } else {
        console.error("Failed to create thread ID.");
      }
    };

    createThread(); // Create a new thread when the component is mounted
  }, []); // Empty dependency array ensures this runs only once when the component is mounted

  // Effect to hide quick actions when messages appear
  useEffect(() => {
    if (messages.length > 0) {
      const timer = setTimeout(() => setShowQuickActions(false), 300); // Matches transition duration
      return () => clearTimeout(timer); // Cleanup
    } else {
      setShowQuickActions(true);
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Adjust textarea height dynamically as user types
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto"; // Reset height
      textarea.style.height = `${textarea.scrollHeight}px`; // Set new height based on content

      // Only show the scrollbar if the height exceeds 150px
      if (textarea.scrollHeight > 150) {
        textarea.style.overflowY = "auto";
      } else {
        textarea.style.overflowY = "hidden";
      }
    }
  }, [input]);

  // Rest of the formatting and message handling functions remain the same

  const formatLatexContent = (content) => {
    return content
      .replace(/\\text\{([^}]+)\}/g, '$1') // Handle \text{} commands
      .split(' ')
      .map((part, index) => {
        if (part.startsWith('\\text{') && part.endsWith('}')) {
          const text = part.slice(6, -1);
          return <span key={index} className="font-sans">{text}</span>;
        } else if (part === '\\times') {
          return <span key={index} className="mx-2">×</span>;
        } else if (part === '=') {
          return <span key={index} className="mx-2">=</span>;
        } else {
          return <span key={index}>{part} </span>;
        }
      });
  };

  const formatMessage = (text) => {
    if (!text) return [];
    const sections = text.split("\n\n");
    return sections.map((section, sectionIndex) => {
      const lines = section.split("\n");
      const formattedLines = lines.map((line, lineIndex) => {
        // Handle math expressions wrapped in \[ \]
        if (line.includes("\\[") && line.includes("\\]")) {
          const mathContent = line.replace(/\\\[|\\\]/g, "").trim();
          return {
            type: "math",
            content: mathContent,
            key: `math-${sectionIndex}-${lineIndex}`,
          };
        }

        // Handle inline math wrapped in \( \)
        if (line.includes("\\(") && line.includes("\\)")) {
          const parts = [];
          const segments = line.split(/(\\[()])/);
          let isInMath = false;

          segments.forEach((segment, i) => {
            if (segment === "\\(") {
              isInMath = true;
            } else if (segment === "\\)") {
              isInMath = false;
            } else if (segment) {
              parts.push({
                type: isInMath ? "inline-math" : "text",
                content: segment,
              });
            }
          });

          return {
            type: "mixed",
            parts,
            key: `mixed-${sectionIndex}-${lineIndex}`,
          };
        }

        // Keep your existing header and bullet handling
        if (line.startsWith("###") || line.startsWith("#")) {
          return {
            type: "header",
            content: line.replace(/^#{1,3}\s/, ""),
            key: `header-${sectionIndex}-${lineIndex}`,
          };
        }

        const isBullet = line.trim().startsWith("•") || line.trim().startsWith("-");
        const boldRegex = /\*\*(.*?)\*\*/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = boldRegex.exec(line)) !== null) {
          if (match.index > lastIndex) {
            parts.push({
              type: "text",
              content: line.slice(lastIndex, match.index),
            });
          }
          parts.push({
            type: "bold",
            content: match[1],
          });
          lastIndex = match.index + match[0].length;
        }
        if (lastIndex < line.length) {
          parts.push({
            type: "text",
            content: line.slice(lastIndex),
          });
        }

        return {
          type: isBullet ? "bullet" : "normal",
          parts: parts.length > 0 ? parts : [{ type: "text", content: line }],
          key: `line-${sectionIndex}-${lineIndex}`,
        };
      });

      return {
        type: "section",
        lines: formattedLines,
        key: `section-${sectionIndex}`,
      };
    });
  };

  const renderFormattedContent = (formatted) => {
    return formatted.map((section) => (
      <div key={section.key} className="space-y-2">
        {section.lines.map((line) => {
          if (line.type === "math") {
            return (
              <Card key={line.key} className="p-4 bg-gradient-to-r from-blue-500/5 to-cyan-500/5 border border-cyan-500/20 my-2">
                <div className="text-lg text-center text-cyan-100 font-mono tracking-wide flex justify-center items-center flex-wrap">
                  {formatLatexContent(line.content)}
                </div>
              </Card>
            );
          }

          if (line.type === "mixed") {
            return (
              <div key={line.key} className="flex flex-wrap items-center gap-1">
                {line.parts.map((part, i) => (
                  <span
                    key={i}
                    className={
                      part.type === "inline-math"
                        ? "px-2 py-1 bg-cyan-500/10 rounded text-cyan-100 font-mono"
                        : ""
                    }
                  >
                    {part.type === "inline-math" ? formatLatexContent(part.content) : part.content}
                  </span>
                ))}
              </div>
            );
          }

          if (line.type === "header") {
            return (
              <h3
                key={line.key}
                className="text-lg font-semibold text-cyan-400 bg-cyan-500/5 px-3 py-2 rounded-lg border border-cyan-500/10"
              >
                {line.content}
              </h3>
            );
          }

          if (line.type === "bullet") {
            return (
              <div key={line.key} className="flex items-start space-x-2 pl-2">
                <ChevronRight className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                <span>
                  {line.parts.map((part, i) => (
                    <span
                      key={i}
                      className={part.type === "bold" ? "font-bold text-cyan-300" : ""}
                    >
                      {part.content}
                    </span>
                  ))}
                </span>
              </div>
            );
          }

          return (
            <div key={line.key}>
              {line.parts.map((part, i) => (
                <span
                  key={i}
                  className={part.type === "bold" ? "font-bold text-cyan-300" : ""}
                >
                  {part.content}
                </span>
              ))}
            </div>
          );
        })}
      </div>
    ));
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    if (!input.trim()) return;

    try {
      setLoading(true);

      const currentThreadId =
        sessionStorage.getItem("chatThreadId") || threadId;

      if (!currentThreadId) {
        console.error("No thread ID available.");
        return;
      }

      // Add the user's message
      setMessages((prev) => [
        ...prev,
        {
          role: "user",
          content: input,
          timestamp: new Date(),
        },
      ]);
      setInput("");

      const decoder = new TextDecoder();
      const response = await fetch(
        "https://dfkt6ybcwwcx2jnmeok3h4nyz40vzgrb.lambda-url.us-east-1.on.aws/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            message: input,
            threadId: currentThreadId,
          }),
        }
      );

      const reader = response.body.getReader();
      let partialMessage = "";
      let hasReceivedFirstChunk = false;

      // Stream the data
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        if (!hasReceivedFirstChunk) {
          setLoading(false); // Stop showing the loading indicator on the first chunk
          hasReceivedFirstChunk = true;
        }

        partialMessage += decoder.decode(value, { stream: true });
        const formatted = formatMessage(partialMessage);

        // Log to check streaming status
        console.log("Received chunk: ", partialMessage);

        setMessages((prev) => {
          const newMessages = [...prev];
          if (newMessages[newMessages.length - 1]?.role === "assistant") {
            // Update the last assistant message
            newMessages[newMessages.length - 1].content = partialMessage;
            newMessages[newMessages.length - 1].formattedContent = formatted;
          } else {
            // Add a new assistant message
            newMessages.push({
              role: "assistant",
              content: partialMessage,
              formattedContent: formatted,
              timestamp: new Date(),
            });
          }
          return newMessages;
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, I encountered an error. Please try again.",
          timestamp: new Date(),
          error: true,
        },
      ]);
    } finally {
      // Ensure setLoading(false) is called even if no chunks are received
      setLoading(false);
    }
  };

  const predefinedQueries = [
    {
      label: "Today's Production",
      prompt: "Show me today's production numbers",
    },
    { label: "Quality Analysis", prompt: "Analyze our quality metrics" },
    { label: "Production Trends", prompt: "What are the production trends?" },
  ];
  return (
    <div className="flex flex-col h-full p-6">
      {/* Scrollable Content Area */}
      <div
        className="flex-1 overflow-y-auto scroll-smooth pr-2 -mr-2 space-y-4"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "#1f2937 transparent",
        }}
      >
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`flex items-start space-x-2 max-w-[85%] ${message.role === "user" ? "flex-row-reverse space-x-reverse" : ""}`}
              >
                <div
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    message.role === "user"
                      ? "bg-gradient-to-r from-blue-500 to-cyan-500"
                      : "bg-gradient-to-r from-slate-600 to-slate-700"
                  }`}
                >
                  {message.role === "user" ? (
                    <User className="w-4 h-4 text-white" />
                  ) : (
                    <Bot className="w-4 h-4 text-white" />
                  )}
                </div>
                <div>
                  <div
                    className={`rounded-2xl px-4 py-2  max-w-3xl ${
                      message.role === "user"
                        ? "bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 text-blue-100"
                        : message.error
                          ? "bg-red-500/10 border border-red-500/20 text-red-100"
                          : "bg-slate-800/60 border border-slate-700/50 text-slate-100"
                    }`}
                  >
                    {message.role === "assistant" &&
                    message.formattedContent ? (
                      renderFormattedContent(message.formattedContent)
                    ) : (
                      <div>{message.content}</div>
                    )}{" "}
                  </div>
                  <span className="text-xs text-slate-500 px-2">
                    {message.timestamp.toLocaleTimeString()}
                  </span>
                </div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex items-center space-x-2 text-slate-400">
              <div className="animate-spin w-4 h-4 border-2 border-slate-500 border-t-cyan-500 rounded-full" />
              <span>Processing...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Quick Actions Section */}
      {showQuickActions && (
        <div
          className={`flex justify-center items-center mb-4 transition-all duration-300 ${
            messages.length > 0 ? "opacity-0 scale-95" : "opacity-100 scale-100"
          }`}
        >
          <div className="flex flex-wrap gap-4 justify-center ">
            {predefinedQueries.map((query, index) => (
              <button
                key={index}
                onClick={() => setInput(query.prompt)}
                className="px-12 py-3 text-sm rounded-lg bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:border-cyan-500/60 transition-all duration-200"
              >
                {query.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Fixed Header Section */}
      <div className="flex justify-center items-center sticky bottom-0 z-10 backdrop-blur-sm py-4">
        {/* Input Form */}
        <form
          onSubmit={handleSendMessage}
          className="flex space-x-2 lg:w-7/12 sm:w-full w-full justify-center items-end"
        >
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={"Ask about production data..."}
            className="flex-1 bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-2 text-slate-100 placeholder-slate-500 resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
            style={{
              maxHeight: "150px", // Max height before scroll
              overflowY: "auto", // Add scrollbar after max height
            }}
            rows={1}
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:shadow-lg hover:shadow-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2"
            style={{
              height: "44px", // Keeps button height fixed
            }}
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>

      {/* Custom Scrollbar Styles */}
      <style jsx global>{`
        /* Webkit (Chrome, Safari, etc.) */
        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: transparent;
        }

        ::-webkit-scrollbar-thumb {
          background-color: #374151;
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background-color: #4b5563;
        }

        /* Firefox */
        * {
          scrollbar-width: thin;
          scrollbar-color: #374151 transparent;
        }

        /* For smooth scrolling on the entire page */
        html {
          scroll-behavior: smooth;
        }
      `}</style>
    </div>
    //
  );
};

export default ChatbotVeiw;
